import { Fragment, useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { Chart as ChartJS, LineElement, PointElement, CategoryScale, LinearScale, Legend, Tooltip } from "chart.js";
import { Line } from "react-chartjs-2";
import { getVideoAnalysis } from "actions/spyAdvancedActions";
import useCallApi from "hook/useCallApi";
import useInViewport from "hook/useInViewport";

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Legend, Tooltip);

const VideoChart = ({ video, getVideoAnalysis }) => {
    const { t } = useTranslation();
    const [lineChartData, setLineChartData] = useState(null);
    const [lineChartOptions, setLineChartOptions] = useState(null);
    const [ref, inView] = useInViewport({ threshold: 0.2, once: true });

    const paramOverview = useMemo(() => {
        return {
            id: video.video_id,
            tag: "basic.videos.trending",
            indicator: "line",
            dateRange: "30",
        };
    }, [video.video_id]);
    const { data, loading } = useCallApi(getVideoAnalysis, paramOverview, inView);
    const videoAnalysis = data?.data;

    useEffect(() => {
        if (!videoAnalysis) return;
        const line1 = videoAnalysis.filter((d) => d.legend === "Likes");
        const line2 = videoAnalysis.filter((d) => d.legend === "Likes Increment");
        const labels = line1.map((d) => d.axis_x);

        setLineChartData({
            labels,
            datasets: [
                {
                    label: "Likes",
                    data: line1.map((d) => d.axis_y),
                    borderColor: "#4F70FD",
                    backgroundColor: "#4F70FD",
                    tension: 0.3,
                    yAxisID: "y",
                    pointRadius: 0,
                    pointHoverRadius: 4,
                },
                {
                    label: "Like Influencers",
                    data: line2.map((d) => d.axis_y),
                    borderColor: "#E04ACB",
                    backgroundColor: "#E04ACB",
                    tension: 0.3,
                    yAxisID: "y1",
                    pointRadius: 0,
                    pointHoverRadius: 4,
                },
            ],
        });

        setLineChartOptions({
            responsive: true,
            plugins: {
                legend: {
                    position: "bottom",
                    labels: {
                        usePointStyle: true,
                        pointStyle: "circle",
                        boxWidth: 10,
                        boxHeight: 10,
                    },
                },
                tooltip: {
                    mode: "index",
                    intersect: false,
                },
                datalabels: { display: false },
            },
            interaction: {
                mode: "nearest",
                intersect: false,
            },
            scales: {
                y: {
                    type: "linear",
                    position: "left",
                    ticks: {
                        callback: (value) => `${(value / 1000).toFixed(1)}K`,
                    },
                    title: {
                        display: true,
                        text: "Video Influencers",
                    },
                },
                y1: {
                    type: "linear",
                    position: "right",
                    grid: {
                        drawOnChartArea: false,
                    },
                    ticks: {
                        callback: (value) => `${(value / 1000).toFixed(1)}K`,
                    },
                    title: {
                        display: true,
                        text: "Live Influencers",
                    },
                },
            },
        });
    }, [videoAnalysis, t]);

    return (
        <Fragment>
            <div className="card mt-5 mb-5 mb-xl-10 pb-8" ref={ref}>
                <div className="card-header border-0 cursor-pointer align-items-center">
                    <div className="card-title m-0">
                        <h3 className="fw-bold m-0">{t("Video Chart")}</h3>
                    </div>
                </div>
                <div>
                    <div className="d-flex justify-content-between align-items-center gap-8 m-4 px-20">
                        {loading ? (
                            <div className="w-100 d-flex justify-content-center">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="sr-only"></span>
                                </div>
                            </div>
                        ) : (
                            lineChartData &&
                            lineChartOptions && <Line type="bar" data={lineChartData} options={lineChartOptions} />
                        )}
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

const mapStateToProps = (state) => ({});
const mapDispatchToProps = { getVideoAnalysis };

export default connect(mapStateToProps, mapDispatchToProps)(VideoChart);
